import { plural } from '@srm/ui/lib/pluralize';
import { capitalize } from '@/utils/capitalize';
import { diffCalendarDays, formatDayMonth } from '@/utils/date';
import type {
    IDocumentStatusBar,
    IStatusBarBranch,
    IStatusBarItem,
    IStatusBarLevel,
    IStatusBarPerson,
    IStatusBarTask,
} from '@models/document/IDocumentStatusBar';

// Общая модель «Статуса тендера» для вкладки карточки, полоски в шапке и поповера календаря: фронт закупки,
// «этап N из M», кто держит, блокировка и даты словами. Чистые функции; «сейчас» — параметром.

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** Тон блокировки: `bad` — просрочка или ошибка маршрута, `wait` — только ожидание (Status@hold). */
export type TenderTone = 'bad' | 'wait';

interface StagePosition {
    level: number;
    item: number;
}

interface TenderFront extends StagePosition {
    levelTitle: string;
    stage: IStatusBarItem;
}

const time = (iso: string): number => new Date(iso).getTime();

export const days = (n: number): string => `${n} ${plural(n, ['день', 'дня', 'дней'])}`;
export const hours = (n: number): string => `${n} ${plural(n, ['час', 'часа', 'часов'])}`;

/** @internal Экспорт — для тестов. Сколько идёт этап: «14-й день», в первые сутки — «3 часа». */
export function formatElapsed(sinceIso: string, now: number): string {
    const ms = Math.max(0, now - time(sinceIso));
    if (ms < DAY) return hours(Math.max(1, Math.round(ms / HOUR)));
    return `${Math.floor(ms / DAY) + 1}-й день`;
}

export interface TenderDue {
    text: string;
    /** `late` — просрочено, `soon` — сегодня или завтра */
    tone?: 'late' | 'soon';
}

/**
 * Срок задачи словами: «срок был 23 октября, просрочено на 10 дней», «срок был сегодня, просрочено», «срок завтра,
 * 3 апреля», «без срока». Просрочку считает сервер; дни просрочки — календарные.
 */
export function formatDue(task: IStatusBarTask, now: number): TenderDue {
    if (!task.dueAt) return { text: 'без срока' };
    const day = formatDayMonth(task.dueAt, now);
    if (task.overdue) {
        const late = diffCalendarDays(task.dueAt, now);
        return {
            text: late > 0 ? `срок был ${day}, просрочено на ${days(late)}` : 'срок был сегодня, просрочено',
            tone: 'late',
        };
    }
    const ahead = -diffCalendarDays(task.dueAt, now);
    if (ahead === 0) return { text: `срок сегодня, ${day}`, tone: 'soon' };
    if (ahead === 1) return { text: `срок завтра, ${day}`, tone: 'soon' };
    return { text: `срок ${day}` };
}

/**
 * Фронт закупки — подэтап со статусом Active (самый продвинутый). Нет Active — закупка завершена или ещё не
 * начата: `undefined`.
 */
export function findFront(levels: readonly IStatusBarLevel[]): TenderFront | undefined {
    for (let level = levels.length - 1; level >= 0; level--) {
        const item = levels[level].childs.findIndex((child) => child.status === 'Active');
        if (item >= 0) return { level, item, levelTitle: levels[level].title, stage: levels[level].childs[item] };
    }
    return undefined;
}

/** Закупка завершена: сервер так сказал или фронта нет и все подэтапы пройдены. */
export const isFinished = (bar: IDocumentStatusBar): boolean =>
    !findFront(bar.levels) &&
    (!!bar.finished || bar.levels.every((level) => level.childs.every((child) => child.status === 'Done')));

/** Сквозной номер подэтапа (с 1) и их общее число — для «этап N из M». */
export function stageNumber(levels: readonly IStatusBarLevel[], pos: StagePosition): { n: number; of: number } {
    let n = 0;
    let of = 0;
    levels.forEach((level, li) => {
        if (li < pos.level) n += level.childs.length;
        of += level.childs.length;
    });
    return { n: n + pos.item + 1, of };
}

/** Сколько ветки стоят на фронте: от самого раннего входа ветки в её текущий подэтап. Нет дат — `undefined`. */
export function frontElapsed(bar: IDocumentStatusBar, now: number): string | undefined {
    const front = findFront(bar.levels);
    if (!front) return undefined;
    const since = (bar.branches ?? [])
        .filter((b) => b.level === front.level && b.item === front.item)
        .reduce<string | undefined>(
            (min, { enteredAt }) => (enteredAt && (!min || time(enteredAt) < time(min)) ? enteredAt : min),
            undefined,
        );
    return since && formatElapsed(since, now);
}

/** Кто исполняет задачу: полное имя одного, «А и Б» для двоих, «15 исполнителей». */
export function performersLabel(performers: readonly IStatusBarPerson[]): string {
    if (performers.length === 0) return 'Исполнитель не назначен';
    if (performers.length === 1) return performers[0].fullName ?? performers[0].name;
    if (performers.length === 2) return `${performers[0].name} и ${performers[1].name}`;
    return `${performers.length} ${plural(performers.length, ['исполнитель', 'исполнителя', 'исполнителей'])}`;
}

/** «ПТК №627»; без номера — название потока. */
export const branchLabel = (branch: IStatusBarBranch): string =>
    branch.number ? `${branch.flowTitle} №${branch.number}` : branch.flowTitle;

/**
 * Ветки, которые «держат» закупку: с задачами людей, ошибкой маршрута, ожиданием или отставанием, и ветки на фронте.
 * Порядок — от самой отстающей. При showPeople=false задачи веток скрыты, блокировки остаются.
 */
export function holdingBranches(bar: IDocumentStatusBar): IStatusBarBranch[] {
    const branches = (bar.branches ?? []).map((b) => (bar.showPeople === false ? { ...b, tasks: null } : b));
    const front = findFront(bar.levels);
    const atFront = (b: IStatusBarBranch) => !!front && b.level === front.level && b.item === front.item;
    const pos = (b: IStatusBarBranch) => stageNumber(bar.levels, b).n;
    return branches
        .filter((b) => atFront(b) || !!(b.tasks?.length || b.routeErrors?.length || b.hold || b.lagging))
        .map((b, index) => ({ b, index, at: pos(b) }))
        .sort((x, y) => x.at - y.at || x.index - y.index)
        .map(({ b }) => b);
}

interface TenderBlocker {
    tone: TenderTone;
    /** Причины по строке — для подсказки */
    reasons: string[];
}

/** Блокировка по набору веток: просрочка или ошибка маршрута — `bad`, только ожидание — `wait`. */
export function blockerOf(branches: readonly IStatusBarBranch[]): TenderBlocker | undefined {
    const reasons: string[] = [];
    let tone: TenderTone | undefined;
    branches.forEach((b) => {
        const prefix = b.isCurrent ? '' : `${branchLabel(b)}: `;
        b.routeErrors?.forEach(() => {
            tone = 'bad';
            reasons.push(`${prefix}маршрут остановлен`);
        });
        b.tasks?.forEach((task) => {
            if (!task.overdue) return;
            tone = 'bad';
            reasons.push(`${prefix}просрочена задача «${task.name}»`);
        });
        if (b.hold) {
            tone ??= 'wait';
            reasons.push(`${prefix}${lowerFirst(b.hold)}`);
        }
    });
    return tone ? { tone, reasons: [...new Set(reasons)].map(capitalize) } : undefined;
}

/** Первая буква строчная, если это не аббревиатура («На доработке» → «на доработке», «ПТК» — как есть). */
export function lowerFirst(text: string): string {
    if (text.length > 1 && text[1] === text[1].toUpperCase() && text[1] !== text[1].toLowerCase()) return text;
    return text.charAt(0).toLowerCase() + text.slice(1);
}

/** Добавка к активному уровню полоски в шапке (проп `activeNote`): сколько идёт фронт и есть ли блокировка. */
export function headerNote(
    bar: IDocumentStatusBar | undefined,
    now: number,
): { elapsed?: string; tone?: TenderTone; reasons?: string[] } {
    if (!bar?.levels?.length) return {};
    const blocker = blockerOf(holdingBranches(bar));
    return { elapsed: frontElapsed(bar, now), tone: blocker?.tone, reasons: blocker?.reasons };
}
